import { useEffect, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `delayMs` has
 * passed without further changes. Useful for search inputs so an expensive
 * filter/search/sort pass runs once the user pauses typing rather than on every
 * keystroke.
 *
 * A `delayMs <= 0` disables debouncing and returns the value as-is (so callers
 * can opt out without changing behavior).
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (delayMs <= 0) {
      return undefined;
    }
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return delayMs <= 0 ? value : debounced;
}
